// src/components/FormField.tsx
import type { ReactElement } from 'react';

// Minimal label + control + error-slot form-field wrappers. First form inputs
// in the app (A1EE-338). Each control is wired to its <label> via id/htmlFor and
// sets aria-invalid + a role="alert" message when `error` is non-empty.

const LABEL = 'mb-1 block text-sm font-medium text-slate-700';
const CONTROL =
  'w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 ' +
  'focus:border-slate-400 focus:outline-none aria-[invalid=true]:border-red-400';
const ERROR = 'mt-1 text-xs text-red-700';

function Error({ error }: { error?: string | null }) {
  if (!error) return null;
  return (
    <p role="alert" className={ERROR}>
      {error}
    </p>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  placeholder?: string;
  required?: boolean;
}): ReactElement {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={CONTROL}
      />
      <Error error={error} />
    </div>
  );
}

export function TextAreaField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  required,
  rows = 4,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  placeholder?: string;
  required?: boolean;
  rows?: number;
}): ReactElement {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={CONTROL}
      />
      <Error error={error} />
    </div>
  );
}

export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  error,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  error?: string | null;
  required?: boolean;
}): ReactElement {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      <select
        id={id}
        value={value}
        aria-invalid={error ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={CONTROL}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Error error={error} />
    </div>
  );
}
